import ky from "ky";
import { useQuery, useMutation } from "@tanstack/react-query";

import { hooks } from "~/lib/ky-utils";

import type { SessionResponse } from "~/api/session";
import type { LogoutResponse } from "~/lib/auth/logout";

import type {
  CompleteAccountRequest,
  CompleteAccountResponse,
} from "~/lib/auth/complete-account";

import { useSite } from "./useSite";
import { useGoogleTagManager } from "./useGoogleTagManager";

/**
 * Response from /api/session endpoint.
 */
export function useSession() {
  const site = useSite();

  return useQuery({
    queryKey: ["session"],
    staleTime: 10 * 60 * 1000, // Do not refetch for 10 minutes.
    queryFn: async () => {
      const res = await ky
        .get("/api/session", { headers: { "X-Site-Handle": site.handle } })
        .json<SessionResponse>();

      return res;
    },
  });
}

/**
 * Status of session.
 */
export function useSessionStatus() {
  return useSession().status;
}

/**
 * Customer from session.
 */
export function useSessionCustomer() {
  return useSession().data?.customer ?? null;
}

/**
 * Alias for the useSessionCustomer hook.
 */
export const useCustomer = useSessionCustomer;

/**
 * Return true if the customer is logged in, false if not,
 * and undefined if session is still loading.
 */
export function useSessionLoggedIn() {
  const status = useSessionStatus();
  const customer = useSessionCustomer();

  if ("pending" === status) return undefined;

  return !!customer;
}

/**
 * Alias for the useSessionLoggedIn hook.
 */
export const useLoggedIn = useSessionLoggedIn;

/**
 * Session ID from session.
 */
export function useSessionId() {
  return useSession().data?.sessionId ?? null;
}

/**
 * Session feature flags from session.
 */
export function useSessionFeatureFlags() {
  return useSession().data?.featureFlags ?? null;
}

/**
 * Alias for the useSessionFeatureFlags hook.
 */
export const useFeatureFlags = useSessionFeatureFlags;

/**
 * Logout the current session.
 */
export function useLogout() {
  const site = useSite();

  const { mutateAsync } = useMutation({
    mutationKey: ["logout"],
    mutationFn: async () =>
      ky
        .post("/api/auth/logout", {
          hooks,
          headers: { "X-Site-Handle": site.handle },
        })
        .json<LogoutResponse>(),
  });

  return mutateAsync;
}

export function useCompleteAccount() {
  const site = useSite();
  // const googleTagManager = useGoogleTagManager();

  const { mutateAsync } = useMutation({
    mutationKey: ["complete-account"],
    mutationFn: async (data: CompleteAccountRequest) =>
      ky
        .post("/api/auth/complete-account", {
          hooks,
          json: data,
          headers: { "X-Site-Handle": site.handle },
        })
        .json<CompleteAccountResponse>(),
    onSuccess: () => {
      /** @todo Send an analytics event? */
      // googleTagManager.push({
      //   event: "sign_up",
      //   properties: {
      //     method: "register_form",
      //   },
      // });
    },
  });

  return mutateAsync;
}
